import { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useQueryClient } from "react-query";
import VnvAssistSplashScreen from "./components/VnvAssistSplashScreen";
import { useVnvAssistStore } from "./store/VnvAssistStore";
import { useCreateVnvAssistRun } from "./services/VnvAssistServices";
import { useSetVnvAssistRecentsConfig } from "src/views/assistant/hooks/useProductConfig";
import { useCreateDialogue } from "src/reusable/Chat/ChatStreaming/services/api";
import {
  useChatStore,
  useActiveDatasetIds,
} from "src/views/assistant/stores/ChatStore";
import { useSyncPersonaToStore } from "src/views/assistant/hooks/useSyncPersonaToStore";
import { NucToast } from "src/reusable/components/Toast";

const VnvAssistSplashPage = () => {
  const history = useHistory();
  const queryClient = useQueryClient();

  // Reset the global VNV store on every splash-page entry.
  useEffect(() => {
    useVnvAssistStore.getState().reset();
  }, []);

  const { config: chatConfig } = useChatStore();
  const activeDatasetIds = useActiveDatasetIds();

  const {
    selectedArtifact,
    selectedDatasetIds,
    getOrCreateDialogue,
    setVnvRunId,
  } = useVnvAssistStore();

  const { mutateAsync: createRun, isLoading: isCreatingRun } =
    useCreateVnvAssistRun();
  const { mutateAsync: createDialogue, isLoading: isCreatingDialogue } =
    useCreateDialogue();

  const { isLoading: isSyncingPersona } = useSyncPersonaToStore({
    allowUrlSync: false,
    ignoreDialogueHistory: true,
  });

  useSetVnvAssistRecentsConfig(queryClient);

  const handleStart = async (): Promise<boolean> => {
    if (!selectedArtifact) {
      NucToast({ message: "Please select an input document.", type: "error" });
      return false;
    }
    if (selectedDatasetIds.length === 0) {
      NucToast({
        message: "Please select at least one reference dataset.",
        type: "error",
      });
      return false;
    }

    try {
      const effectiveDialogueId = await getOrCreateDialogue(async () => {
        const id = await createDialogue({
          conversation: {
            model_id: chatConfig?.modelId ?? null,
            model_version_number: chatConfig?.modelVersionNum ?? null,
            search_datasets: activeDatasetIds,
            n_search_results: chatConfig?.topSearchResults || 5,
            model_inference_data: {
              infer_data: JSON.stringify([]),
              stream: true,
              streaming_response_required: true,
              text_gen_model_params: chatConfig?.chatControlMetadata,
            },
            stream: true,
            tool_calling: true,
            chat_config_id: chatConfig?.chatConfigId || null,
            product: "vnv-assist",
          },
        });
        return id;
      });

      const runId = await createRun({
        dialogue_id: effectiveDialogueId,
        input_artifact_id: selectedArtifact.id,
        dataset_ids: selectedDatasetIds,
      });

      setVnvRunId(runId);
      history.push(`/vnv-assist/chat/${runId}/${effectiveDialogueId}`);
      return true;
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Unknown error";
      NucToast({ message: `Failed to start: ${msg}`, type: "error" });
      return false;
    }
  };

  if (isSyncingPersona) return null;

  return (
    <VnvAssistSplashScreen
      onStart={handleStart}
      isLoading={isCreatingRun || isCreatingDialogue}
    />
  );
};

export default VnvAssistSplashPage;
